import React, { useState } from 'react';
import { FileText, CheckSquare, Settings, Wrench, Shield, User, Download, ChevronDown, Bird, Zap, Microscope, Droplets } from 'lucide-react';
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const SEO_KEYWORDS = ["opleverdocument zonnepanelen", "conformiteitsverklaring", "onderhoud zonnepanelen", "Sol-Vi"];

// Styled Components for the Document
const DocumentWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-gradient-subtle">
    <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-lg border border-border">
        {children}
      </div>
    </main>
  </div>
);

const DocumentHeader = () => (
  <div className="p-8 border-b border-border bg-muted/30 rounded-t-2xl text-center">
    <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
      <FileText size={32} />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Opleverdocument Installatie</h1>
    <p className="mt-2 text-lg text-muted-foreground">Verklaring van Conformiteit & Onderhoudsadvies</p>
  </div>
);

const Section = ({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) => (
  <div className="p-8 border-b border-border last:border-b-0">
    <h2 className="text-2xl font-semibold text-foreground mb-6 flex items-center">
      <Icon className="w-7 h-7 mr-3 text-primary" />
      {title}
    </h2>
    <div className="space-y-6">
      {children}
    </div>
  </div>
);

const InputField = ({ id, label, value, onChange, type = "text", placeholder = "" }: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
}) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-medium text-muted-foreground">{label}</Label>
    <Input
      type={type}
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full"
    />
  </div>
);

const CheckboxItem = ({ label, name, checked, onChange }: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="flex items-start space-x-3">
    <Checkbox
      id={name}
      name={name}
      checked={checked}
      onCheckedChange={(checked) => {
        const event = {
          target: { name, type: 'checkbox', checked }
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }}
      className="mt-1"
    />
    <Label htmlFor={name} className="text-sm leading-relaxed font-medium text-foreground cursor-pointer">
      {label}
    </Label>
  </div>
);

const AccordionItem = ({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border border-border">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex justify-between items-center text-left">
              <span className="flex items-center">
                <Icon className="w-5 h-5 mr-3 text-primary" />
                {title}
              </span>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="prose prose-sm max-w-none text-foreground">
              {children}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const SignatureField = ({ id, name, label, value, onChange, placeholder }: {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-medium text-muted-foreground">{label}</Label>
    <Input
      type="text"
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="bg-muted/30 border-0 border-b-2 border-border focus:border-primary rounded-none text-lg font-bold"
      style={{ fontFamily: 'Caveat, cursive' }}
    />
  </div>
);

const SignatureSection = ({ formData, onChange }: {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-4">
    <div>
      <h3 className="font-semibold text-foreground mb-6">Installateur</h3>
      <div className="space-y-6">
        <SignatureField
          id="installateurHandtekening"
          name="installateurHandtekening"
          label="Handtekening (typ uw naam)"
          value={formData.installateurHandtekening}
          onChange={onChange}
          placeholder="Typ hier uw volledige naam"
        />
        <InputField
          id="installateurNaamGetekend"
          label="Naam (in blokletters)"
          value={formData.installateurNaamGetekend}
          onChange={onChange}
        />
      </div>
    </div>
    <div>
      <h3 className="font-semibold text-foreground mb-6">Klant</h3>
      <div className="space-y-6">
        <SignatureField
          id="klantHandtekening"
          name="klantHandtekening"
          label="Handtekening (typ uw naam)"
          value={formData.klantHandtekening}
          onChange={onChange}
          placeholder="Typ hier uw volledige naam"
        />
        <InputField
          id="klantNaamGetekend"
          label="Naam (in blokletters)"
          value={formData.klantNaamGetekend}
          onChange={onChange}
        />
      </div>
    </div>
  </div>
);

export default function OpleverDocumentPage() {
  const [formData, setFormData] = useState({
    installateurBedrijf: '',
    installateurMonteur: '',
    datumOplevering: '',
    klantNaam: '',
    installatieAdres: '',
    eanCode: '',
    omvormerMerkType: '',
    omvormerVermogen: '',
    panelenMerkType: '',
    panelenAantal: '',
    panelenPiekvermogen: '',
    thuisbatterij: '',
    vogelwering: false,
    installateurHandtekening: '',
    installateurNaamGetekend: '',
    klantHandtekening: '',
    klantNaamGetekend: '',
    verklaring1: false,
    verklaring2: false,
    verklaring3: false,
    verklaring4: false,
    bevestiging1: false,
    bevestiging2: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Add Google Font for signature
  React.useEffect(() => {
    if (!document.getElementById('google-font-caveat')) {
      const link = document.createElement('link');
      link.id = 'google-font-caveat';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <>
      <SEO
        title="Opleverdocument Zonnepanelen Installatie - Sol-Vi"
        description="Officieel opleverdocument met conformiteitsverklaring en onderhoudsadvies voor uw nieuwe zonnepanelen-installatie."
        keywords={SEO_KEYWORDS.join(', ')}
        canonical="https://sol-vi.nl/opleverdocument"
      />
      <Header />

      <DocumentWrapper>
        <DocumentHeader />

        <div className="relative">
          <Button 
            onClick={() => window.print()} 
            className="absolute top-4 right-4 print:hidden"
            variant="outline"
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          
          <Section title="1. Projectgegevens" icon={User}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <InputField id="installateurBedrijf" label="Bedrijfsnaam Installateur" value={formData.installateurBedrijf} onChange={handleInputChange} />
              <InputField id="installateurMonteur" label="Naam Gecertificeerd Monteur" value={formData.installateurMonteur} onChange={handleInputChange} />
              <InputField id="datumOplevering" label="Datum van Oplevering" value={formData.datumOplevering} onChange={handleInputChange} type="date" />
              <InputField id="klantNaam" label="Naam Klant" value={formData.klantNaam} onChange={handleInputChange} />
              <InputField id="installatieAdres" label="Adres Installatie" value={formData.installatieAdres} onChange={handleInputChange} />
              <InputField id="eanCode" label="EAN-code Aansluiting" value={formData.eanCode} onChange={handleInputChange} />
            </div>
          </Section>

          <Section title="2. Specificaties van de Opwekinstallatie" icon={Settings}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <InputField id="omvormerMerkType" label="Merk en Type Omvormer(s)" value={formData.omvormerMerkType} onChange={handleInputChange} />
              <InputField id="omvormerVermogen" label="AC-vermogen Omvormer(s) (kW)" value={formData.omvormerVermogen} onChange={handleInputChange} type="number" />
              <InputField id="panelenMerkType" label="Merk en Type Zonnepanelen" value={formData.panelenMerkType} onChange={handleInputChange} />
              <InputField id="panelenAantal" label="Aantal Panelen" value={formData.panelenAantal} onChange={handleInputChange} type="number" />
              <InputField id="panelenPiekvermogen" label="Totaal Piekvermogen (Wp)" value={formData.panelenPiekvermogen} onChange={handleInputChange} type="number" />
              <InputField id="thuisbatterij" label="Eventuele Thuisbatterij" value={formData.thuisbatterij} onChange={handleInputChange} />
            </div>
          </Section>

          <Section title="3. Aanvullende Werkzaamheden en Onderhoudsadvies" icon={Wrench}>
            <div className="space-y-6">
              <CheckboxItem name="vogelwering" label="Vogelwering / Dakgootbescherming is geïnstalleerd." checked={formData.vogelwering} onChange={handleInputChange}/>
              
              <div className="pt-4">
                <h3 className="text-xl font-semibold text-foreground mb-4">Onderhoudsadvies en Verantwoordelijkheden</h3>
                <div className="space-y-4">
                  <AccordionItem title="Vogelwering en Ongedierte" icon={Bird}>
                    <p><strong>Advies:</strong> Controleer de vogelwering periodiek op beschadigingen en zorg dat de borstels schoon en vrij van vuil zijn.</p>
                    <p><strong>Waarom is dit cruciaal?</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Verminderde effectiviteit:</strong> Een ophoping van vogelpoep, bladeren en ander vuil kan de borstels van de wering samendrukken. Hierdoor neemt de dichtheid af en kunnen er alsnog openingen ontstaan waar klein ongedierte of vogels doorheen kunnen komen.</li>
                      <li><strong>Brandgevaar:</strong> Vogels bouwen nesten met droge, brandbare materialen onder uw panelen. Dit belemmert de ventilatie, waardoor de panelen oververhit kunnen raken en in het ergste geval brand kan ontstaan.</li>
                      <li><strong>Schade:</strong> Ongedierte kan aan de bekabeling knagen, wat leidt tot storingen en gevaarlijke situaties.</li>
                      <li><strong>Verzekering & Aansprakelijkheid:</strong> Schade die voortkomt uit ongedierte (zoals kabelbreuk of brand door nestmateriaal) kan door een verzekeraar worden beschouwd als achterstallig onderhoud. Als eigenaar bent u verantwoordelijk voor het voorkomen van deze risico's. Verwaarlozing, inclusief het niet schoonhouden van de wering, kan leiden tot een afwijzing van een schadeclaim.</li>
                    </ul>
                  </AccordionItem>
                  
                  <AccordionItem title="Visuele Inspectie" icon={Microscope}>
                    <p><strong>Advies:</strong> Loop 2 tot 4 keer per jaar om uw huis en inspecteer de panelen (vanaf de grond) op zichtbare vervuiling (vogelpoep, blad), beschadigingen (scheuren) of loszittende onderdelen.</p>
                    <p><strong>Waarom is dit cruciaal?</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Vroegtijdige Detectie:</strong> U signaleert problemen voordat ze tot grote rendementsverliezen of gevaarlijke situaties leiden.</li>
                      <li><strong>Verzekering & Aansprakelijkheid:</strong> U voldoet aan uw 'zorgplicht' als eigenaar. Het kunnen aantonen dat u de installatie regelmatig controleert, staat sterker bij een eventuele garantie- of verzekeringsclaim. De installateur is verantwoordelijk voor de installatie, maar u bent verantwoordelijk voor het melden van zichtbare gebreken na oplevering.</li>
                    </ul>
                  </AccordionItem>
                  
                  <AccordionItem title="Reiniging van Panelen" icon={Droplets}>
                    <p><strong>Advies:</strong> Reinig de panelen alleen wanneer het rendement zichtbaar afneemt door vervuiling. Gebruik uitsluitend osmosewater en een zachte borstel. Reinig nooit in direct zonlicht.</p>
                    <p><strong>Waarom is dit cruciaal?</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Rendement:</strong> Een schone installatie presteert optimaal. Hardnekkig vuil kan 'hotspots' veroorzaken die panelen permanent kunnen beschadigen.</li>
                      <li><strong>Voorkomen van Schade:</strong> Agressieve schoonmaakmiddelen of schurende materialen kunnen de beschermende coating van de panelen aantasten, wat de levensduur verkort en garantie kan doen vervallen.</li>
                      <li><strong>Verzekering & Aansprakelijkheid:</strong> Schade veroorzaakt door onjuiste reiniging valt niet onder de garantie en wordt waarschijnlijk niet gedekt door uw verzekering. U bent zelf verantwoordelijk voor het correct (laten) uitvoeren van de reiniging.</li>
                    </ul>
                  </AccordionItem>
                  
                  <AccordionItem title="Monitoring van Opbrengst" icon={Zap}>
                    <p><strong>Advies:</strong> Controleer minimaal maandelijks de opbrengst via de app van uw omvormer. Vergelijk de opbrengst met voorgaande periodes en let op onverklaarbare, plotselinge dalingen.</p>
                    <p><strong>Waarom is dit cruciaal?</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Snel Inzicht:</strong> Dit is de snelste manier om een technisch defect aan de omvormer of een probleem met een paneel te detecteren.</li>
                      <li><strong>Beperken van Verlies:</strong> Hoe sneller u een storing meldt, hoe sneller deze kan worden verholpen en hoe minder opbrengst u misloopt.</li>
                      <li><strong>Verzekering & Aansprakelijkheid:</strong> Het is uw verantwoordelijkheid als eigenaar om de prestaties van uw systeem te monitoren en storingen tijdig te melden bij uw installateur. Langdurig negeren van een storing kan als nalatigheid worden gezien.</li>
                    </ul>
                  </AccordionItem>
                </div>
              </div>
            </div>
          </Section>
          
          <Section title="4. Toelichting Technische Normen" icon={Shield}>
            <div className="prose prose-sm max-w-none text-foreground">
              <p>De installateur heeft de volgende regels met u doorgenomen. Deze zijn essentieel om het elektriciteitsnet voor iedereen veilig en betrouwbaar te houden.</p>
              <h4 className="text-lg font-semibold">A. Europese Verordening (EU) 2016/631 - "Requirements for Generators" (RfG)</h4>
              <p>Dit zijn de spelregels voor de gehele EU. Uw omvormer helpt actief mee aan een stabiel net door slim te reageren op schommelingen en verbonden te blijven bij kortstondige storingen.</p>
              <h4 className="text-lg font-semibold">B. De Nederlandse Netcode Elektriciteit</h4>
              <p>Dit is de Nederlandse uitwerking van de Europese regels. Het garandeert dat uw omvormer specifiek is goedgekeurd voor de Nederlandse markt.</p>
            </div>
          </Section>

          <Section title="5. Verklaring Installateur" icon={CheckSquare}>
            <div className="space-y-4">
              <CheckboxItem name="verklaring1" label="De installatie voldoet aan de Europese Verordening (RfG) en de Nederlandse Netcode Elektriciteit." checked={formData.verklaring1} onChange={handleInputChange} />
              <CheckboxItem name="verklaring2" label="Uitsluitend goedgekeurde en gecertificeerde materialen zijn gebruikt." checked={formData.verklaring2} onChange={handleInputChange} />
              <CheckboxItem name="verklaring3" label="De technische normen én de gedetailleerde onderhoudsadviezen zijn mondeling toegelicht aan de klant." checked={formData.verklaring3} onChange={handleInputChange} />
              <CheckboxItem name="verklaring4" label="De installatie is correct aangemeld bij de netbeheerder via energieleveren.nl." checked={formData.verklaring4} onChange={handleInputChange} />
            </div>
          </Section>
          
          <Section title="6. Bevestiging door Klant" icon={CheckSquare}>
            <div className="space-y-4">
              <CheckboxItem name="bevestiging1" label="De installateur heeft de technische normen, het belang daarvan, en de onderhoudsadviezen (inclusief mijn verantwoordelijkheden t.a.v. verzekering) aan mij uitgelegd." checked={formData.bevestiging1} onChange={handleInputChange} />
              <CheckboxItem name="bevestiging2" label="De installatie is naar tevredenheid opgeleverd." checked={formData.bevestiging2} onChange={handleInputChange} />
            </div>
          </Section>

          <Section title="7. Ondertekening" icon={FileText}>
            <SignatureSection formData={formData} onChange={handleInputChange} />
          </Section>
        </div>
      </DocumentWrapper>
      
      <Footer />
    </>
  );
}